/**
 * Admin moderation page — triage user-to-user reports.
 *
 * Route: `/admin/moderation`
 * Requires authentication: Yes (admin only)
 */

import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  fetchModerationReportDetail,
  fetchModerationReports,
  fetchModerationSummary,
  updateModerationReport,
  type ReportDetail,
  type ReportListItem,
  type ReportStatus,
  type ReportSummary,
} from '@/lib/moderationApi';

const PAGE_SIZE = 20;

const STATUS_OPTIONS: { value: ReportStatus | 'all'; labelKey: string; fallback: string }[] = [
  { value: 'all', labelKey: 'admin.moderation.status.all', fallback: 'All' },
  { value: 'pending', labelKey: 'admin.moderation.status.pending', fallback: 'Pending' },
  { value: 'reviewed', labelKey: 'admin.moderation.status.reviewed', fallback: 'Reviewed' },
  { value: 'actioned', labelKey: 'admin.moderation.status.actioned', fallback: 'Actioned' },
  { value: 'dismissed', labelKey: 'admin.moderation.status.dismissed', fallback: 'Dismissed' },
];

function statusVariant(status: ReportStatus): 'default' | 'secondary' | 'destructive' | 'outline' {
  switch (status) {
    case 'pending':
      return 'destructive';
    case 'reviewed':
      return 'secondary';
    case 'actioned':
      return 'default';
    case 'dismissed':
      return 'outline';
    default:
      return 'outline';
  }
}

function DetailField({ label, value }: Readonly<{ label: string; value: string }>) {
  return (
    <div>
      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </p>
      <p className="mt-1 whitespace-pre-wrap text-sm text-foreground">{value}</p>
    </div>
  );
}

export default function AdminModerationPage() {
  const { t } = useTranslation();
  const [summary, setSummary] = useState<ReportSummary | null>(null);
  const [list, setList] = useState<ReportListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [statusFilter, setStatusFilter] = useState<ReportStatus | 'all'>('pending');
  const [listLoading, setListLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);

  const [detailOpen, setDetailOpen] = useState(false);
  const [detail, setDetail] = useState<ReportDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [adminNotes, setAdminNotes] = useState('');
  const [actionTaken, setActionTaken] = useState('');
  const [nextStatus, setNextStatus] = useState<ReportStatus>('reviewed');
  const [saving, setSaving] = useState(false);

  const loadSummary = useCallback(async () => {
    const data = await fetchModerationSummary();
    setSummary(data);
  }, []);

  const loadList = useCallback(
    async (nextPage: number, nextStatusFilter: ReportStatus | 'all') => {
      setListLoading(true);
      const data = await fetchModerationReports(
        nextPage,
        PAGE_SIZE,
        nextStatusFilter === 'all' ? undefined : nextStatusFilter,
      );
      if (data) {
        setList([...data.items]);
        setTotal(data.total);
        setPage(data.page);
        setTotalPages(data.totalPages);
      }
      setListLoading(false);
    },
    [],
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setInitialLoading(true);
      await loadSummary();
      await loadList(1, statusFilter);
      if (!cancelled) setInitialLoading(false);
    })();
    return () => {
      cancelled = true;
    };
    // statusFilter intentionally not in deps; changing it goes through handleStatusChange
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleStatusChange = (value: string) => {
    const next = value as ReportStatus | 'all';
    setStatusFilter(next);
    loadList(1, next);
  };

  const openDetail = async (id: string) => {
    setDetailOpen(true);
    setDetail(null);
    setDetailLoading(true);
    const d = await fetchModerationReportDetail(id);
    setDetail(d);
    if (d) {
      setAdminNotes(d.adminNotes ?? '');
      setActionTaken(d.actionTaken ?? '');
      setNextStatus(d.status === 'pending' ? 'reviewed' : d.status);
    }
    setDetailLoading(false);
  };

  const handleSave = async () => {
    if (!detail) return;
    setSaving(true);
    const updated = await updateModerationReport(detail.id, {
      status: nextStatus,
      adminNotes,
      actionTaken,
    });
    setSaving(false);
    if (updated) {
      setDetail(updated);
      toast.success(
        t('admin.moderation.detail.saved', { defaultValue: 'Report updated.' }),
      );
      await Promise.all([loadSummary(), loadList(page, statusFilter)]);
    }
  };

  const formatDate = (iso: string | null | undefined) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleString();
    } catch {
      return iso;
    }
  };

  if (initialLoading) {
    return (
      <div className="flex items-center justify-center py-16 text-muted-foreground">
        <Loader2 className="h-8 w-8 animate-spin" aria-hidden />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-bold text-foreground">
          {t('admin.moderation.title', { defaultValue: 'Moderation' })}
        </h2>
        <p className="mt-1 text-muted-foreground">
          {t('admin.moderation.subtitle', {
            defaultValue:
              'Review and act on user-submitted reports of inappropriate behaviour in Partner Finder and chat.',
          })}
        </p>
      </div>

      {summary && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-6">
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>
                {t('admin.moderation.summary.total', { defaultValue: 'Total' })}
              </CardDescription>
              <CardTitle className="text-3xl tabular-nums">{summary.totalCount}</CardTitle>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>
                {t('admin.moderation.summary.pending', { defaultValue: 'Pending' })}
              </CardDescription>
              <CardTitle className="text-3xl tabular-nums text-destructive">
                {summary.pendingCount}
              </CardTitle>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>
                {t('admin.moderation.summary.reviewed', { defaultValue: 'Reviewed' })}
              </CardDescription>
              <CardTitle className="text-3xl tabular-nums">{summary.reviewedCount}</CardTitle>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>
                {t('admin.moderation.summary.actioned', { defaultValue: 'Actioned' })}
              </CardDescription>
              <CardTitle className="text-3xl tabular-nums">{summary.actionedCount}</CardTitle>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>
                {t('admin.moderation.summary.dismissed', { defaultValue: 'Dismissed' })}
              </CardDescription>
              <CardTitle className="text-3xl tabular-nums">{summary.dismissedCount}</CardTitle>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>
                {t('admin.moderation.summary.last7Days', { defaultValue: 'Last 7 days' })}
              </CardDescription>
              <CardTitle className="text-3xl tabular-nums">{summary.last7DaysCount}</CardTitle>
            </CardHeader>
          </Card>
        </div>
      )}

      <Card>
        <CardHeader>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <CardTitle>
                {t('admin.moderation.table.title', { defaultValue: 'Reports' })}
              </CardTitle>
              <CardDescription>
                {t('admin.moderation.table.description', {
                  defaultValue: '{{count}} total',
                  count: total,
                })}
              </CardDescription>
            </div>
            <div className="w-full sm:w-48">
              <Label htmlFor="status-filter" className="sr-only">
                {t('admin.moderation.filter.status', { defaultValue: 'Status' })}
              </Label>
              <Select value={statusFilter} onValueChange={handleStatusChange}>
                <SelectTrigger id="status-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {t(opt.labelKey, { defaultValue: opt.fallback })}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {listLoading ? (
            <div className="flex justify-center py-8 text-muted-foreground">
              <Loader2 className="h-6 w-6 animate-spin" aria-hidden />
            </div>
          ) : list.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">
              {t('admin.moderation.table.empty', {
                defaultValue: 'No reports match this filter.',
              })}
            </p>
          ) : (
            <div className="overflow-x-auto rounded-md border border-border/60">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('admin.moderation.table.date', { defaultValue: 'Date' })}</TableHead>
                    <TableHead>{t('admin.moderation.table.reporter', { defaultValue: 'Reporter' })}</TableHead>
                    <TableHead>{t('admin.moderation.table.reported', { defaultValue: 'Reported user' })}</TableHead>
                    <TableHead>{t('admin.moderation.table.category', { defaultValue: 'Category' })}</TableHead>
                    <TableHead>{t('admin.moderation.table.status', { defaultValue: 'Status' })}</TableHead>
                    <TableHead className="w-[100px]">
                      {t('admin.moderation.table.actions', { defaultValue: 'Actions' })}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {list.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell className="whitespace-nowrap text-xs">
                        {formatDate(row.createdAt)}
                      </TableCell>
                      <TableCell className="text-sm">{row.reporterUserName ?? '—'}</TableCell>
                      <TableCell className="text-sm">{row.reportedUserName ?? '—'}</TableCell>
                      <TableCell className="text-xs">
                        {t(`moderation.report.categories.${row.category}`, {
                          defaultValue: row.category.replace(/_/g, ' '),
                        })}
                      </TableCell>
                      <TableCell>
                        <Badge variant={statusVariant(row.status)}>
                          {t(`admin.moderation.status.${row.status}`, { defaultValue: row.status })}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => openDetail(row.id)}
                        >
                          {t('admin.moderation.table.view', { defaultValue: 'View' })}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {totalPages > 1 && (
            <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
              <p className="text-xs text-muted-foreground">
                {t('admin.moderation.pagination.page', {
                  defaultValue: 'Page {{page}} of {{totalPages}}',
                  page,
                  totalPages,
                })}
              </p>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={page <= 1 || listLoading}
                  onClick={() => loadList(page - 1, statusFilter)}
                >
                  {t('common.previous', { defaultValue: 'Previous' })}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages || listLoading}
                  onClick={() => loadList(page + 1, statusFilter)}
                >
                  {t('common.next', { defaultValue: 'Next' })}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {t('admin.moderation.detail.title', { defaultValue: 'Report detail' })}
            </DialogTitle>
            <DialogDescription>
              {t('admin.moderation.detail.description', {
                defaultValue:
                  'Encrypted message content is not stored in plain text and cannot be displayed here. Decide based on the report description and metadata.',
              })}
            </DialogDescription>
          </DialogHeader>

          {detailLoading && (
            <div className="flex justify-center py-8 text-muted-foreground">
              <Loader2 className="h-6 w-6 animate-spin" aria-hidden />
            </div>
          )}

          {!detailLoading && detail && (
            <div className="space-y-6">
              <div className="grid gap-4 sm:grid-cols-2">
                <DetailField
                  label={t('admin.moderation.detail.submittedAt', { defaultValue: 'Submitted' })}
                  value={formatDate(detail.createdAt)}
                />
                <DetailField
                  label={t('admin.moderation.detail.category', { defaultValue: 'Category' })}
                  value={t(`moderation.report.categories.${detail.category}`, {
                    defaultValue: detail.category.replace(/_/g, ' '),
                  })}
                />
                <DetailField
                  label={t('admin.moderation.detail.reporter', { defaultValue: 'Reporter' })}
                  value={detail.reporterUserName ?? '—'}
                />
                <DetailField
                  label={t('admin.moderation.detail.reported', { defaultValue: 'Reported user' })}
                  value={detail.reportedUserName ?? '—'}
                />
                <DetailField
                  label={t('admin.moderation.detail.messageId', { defaultValue: 'Message ID' })}
                  value={detail.messageId ?? '—'}
                />
                <DetailField
                  label={t('admin.moderation.detail.encrypted', { defaultValue: 'Message encrypted' })}
                  value={
                    detail.messageId
                      ? detail.messageIsEncrypted
                        ? t('common.yes', { defaultValue: 'Yes' })
                        : t('common.no', { defaultValue: 'No' })
                      : '—'
                  }
                />
              </div>

              <DetailField
                label={t('admin.moderation.detail.descriptionLabel', {
                  defaultValue: 'Reporter description',
                })}
                value={detail.description}
              />

              {detail.reviewedAt && (
                <div className="grid gap-4 sm:grid-cols-2 rounded-md border border-border/60 bg-muted/30 p-4">
                  <DetailField
                    label={t('admin.moderation.detail.reviewedBy', { defaultValue: 'Reviewed by' })}
                    value={detail.reviewedByUserName ?? '—'}
                  />
                  <DetailField
                    label={t('admin.moderation.detail.reviewedAt', { defaultValue: 'Reviewed at' })}
                    value={formatDate(detail.reviewedAt)}
                  />
                </div>
              )}

              <div className="space-y-3">
                <div>
                  <Label htmlFor="next-status">
                    {t('admin.moderation.detail.statusLabel', { defaultValue: 'Status' })}
                  </Label>
                  <Select
                    value={nextStatus}
                    onValueChange={(v) => setNextStatus(v as ReportStatus)}
                  >
                    <SelectTrigger id="next-status" className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">
                        {t('admin.moderation.status.pending', { defaultValue: 'Pending' })}
                      </SelectItem>
                      <SelectItem value="reviewed">
                        {t('admin.moderation.status.reviewed', { defaultValue: 'Reviewed' })}
                      </SelectItem>
                      <SelectItem value="actioned">
                        {t('admin.moderation.status.actioned', { defaultValue: 'Actioned' })}
                      </SelectItem>
                      <SelectItem value="dismissed">
                        {t('admin.moderation.status.dismissed', { defaultValue: 'Dismissed' })}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="action-taken">
                    {t('admin.moderation.detail.actionTakenLabel', {
                      defaultValue: 'Action taken (e.g. warned, suspended)',
                    })}
                  </Label>
                  <Textarea
                    id="action-taken"
                    value={actionTaken}
                    onChange={(e) => setActionTaken(e.target.value)}
                    rows={2}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="admin-notes">
                    {t('admin.moderation.detail.notesLabel', { defaultValue: 'Internal notes' })}
                  </Label>
                  <Textarea
                    id="admin-notes"
                    value={adminNotes}
                    onChange={(e) => setAdminNotes(e.target.value)}
                    rows={4}
                    className="mt-1"
                  />
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="ghost" onClick={() => setDetailOpen(false)} disabled={saving}>
              {t('common.close', { defaultValue: 'Close' })}
            </Button>
            <Button onClick={handleSave} disabled={saving || !detail}>
              {saving
                ? t('admin.moderation.detail.saving', { defaultValue: 'Saving…' })
                : t('admin.moderation.detail.save', { defaultValue: 'Save changes' })}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
